import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PDFDocument } from "pdf-lib";

const apiUrl = process.env.REACT_APP_API_URL;

function ViewEmail() {
  const { emailId } = useParams();
  const [emailDetails, setEmailDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const [outlookLoading, setOutlookLoading] = useState(false);
  const [outlookButtonDisabled, setOutlookButtonDisabled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    const fetchEmailDetails = async () => {
      setLoading(true);
      const token = localStorage.getItem("accessToken");

      try {
        const response = await fetch(
          `${apiUrl}fetch-email-template/${emailId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();

        if (response.ok) {
          // Set email details and content
          setEmailDetails(data.data);

          const { docBase64 } = data.pdfFiles;

          if (docBase64) {
            // Convert base64 PDFs to blobs
            const docBlob = base64ToBlob(docBase64, "application/pdf");
            setPdfBlob(docBlob);
          } else {
            setError("Failed to load PDF files");
          }
        } else {
          setError(data.message || "Failed to fetch email details");
        }
      } catch (error) {
        setError("An unexpected error occurred while fetching email details");
        console.error("Error fetching email details:", error);
      } finally {
        setLoading(false);
      }
    };

    // Utility function to convert base64 to Blob
    const base64ToBlob = (base64, contentType = "") => {
      try {
        const sanitizedBase64 = base64.replace(/\s/g, "");
        const binaryString = atob(sanitizedBase64); 
        const len = binaryString.length;
        const bytes = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return new Blob([bytes], { type: contentType });
      } catch (error) {
        console.error("Error converting base64 to Blob:", error);
        throw new Error("Invalid base64 string");
      }
    };
    fetchEmailDetails();
  }, [emailId]);

  // Button Click Event to Open PDF
  const handleOpenPDF = () => {
    if (!pdfBlob) {
      setError("PDF is not available.");
      return;
    }
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl);
  };

  const handleOpenInOutlook = async () => {
    if (emailId) {
      try {
        setOutlookLoading(true);
        setOutlookButtonDisabled(true); // Disable the button after clicking
        const token = localStorage.getItem("accessToken");
        const response = await fetch(
          `${apiUrl}create-outlook-draft/${emailId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const outlookUrl = data.url;
          if (outlookUrl) {
            window.open(outlookUrl, "_blank");
          } else {
            console.error("No URL provided in the response");
          }
        } else {
          console.error("Failed to create Outlook draft");
        }
      } catch (error) {
        console.error("Error creating Outlook draft:", error);
      } finally {
        setOutlookLoading(false);
      }
    } else {
      console.error("No email ID provided");
    }
  };

  const handleOpenOutlookDraft = () => {
    window.open("https://outlook.office.com/mail/drafts", "_blank");
  };

  const handleCancel = () => {
    navigate("/manage-emails");
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => navigate("/manage-emails")}
        className="btn btn-outline-dark mb-3"
      >
        <i className="bi bi-chevron-left"></i> Back
      </button>
      {error && (
        <div className="alert alert-danger mt-2" role="alert">
          {error}
        </div>
      )}

      <div className="dashboard-title mb-3">
        <h1>View Email</h1>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <div className="d-flex flex-column flex-lg-row">
            <div className="me-lg-4 mb-2 mb-lg-0">
              <strong>Name:</strong> {emailDetails?.emailName}
            </div>
            <div>
              <strong>Control No:</strong> {emailDetails?.ControlNo}
            </div>
          </div>
        </div>
        <div>
          {pdfBlob && (
            <button
              onClick={handleOpenPDF} // Call the function here
              className="btn btn-primary"
            >
              View PDF
            </button>
          )}
        </div>
      </div>

      <hr className="mb-4" />

      <div className="my-3">
        <div className="bg-white p-3 rounded-3">
          <div
            dangerouslySetInnerHTML={{ __html: emailDetails?.emailTemplate }}
          ></div>
        </div>
      </div>

      {message && (
        <div className="alert alert-success" role="alert">
          {message}
        </div>
      )}

      <div className="d-grid gap-2 d-md-flex justify-content-md-end">
        {emailDetails?.status === "Draft" && !emailDetails?.draftId && (
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleOpenInOutlook}
            disabled={outlookLoading || outlookButtonDisabled}
          >
            {outlookLoading ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
              />
            ) : (
              "Open in Outlook"
            )}
          </button>
        )}
        {emailDetails?.status === "In Outlook" && emailDetails?.draftId && (
          <button
            className="btn btn-primary"
            type="button"
            onClick={handleOpenOutlookDraft}
          >
            Open Outlook Drafts
          </button>
        )}
        <button
          className="btn btn-warning"
          type="button"
          onClick={handleCancel}
        >
          Close
        </button>
      </div>
    </>
  );
}

export default ViewEmail;